import React, { useState, useEffect, useRef } from 'react';
import { 
  MoreVertical, 
  Trash2, 
  Edit3, 
  CheckSquare, 
  CheckCircle, 
  Share2, 
  Star, 
  StarOff,
  Users,
  Eye,
  UserMinus,
  Circle,
  UserCheck
} from 'lucide-react';
import { apiService } from '../services/apiService';
import ConfirmModal from './ConfirmModal';
import { useLanguage } from '../contexts/LanguageContext';

const ShoppingListCard = ({ 
  list, 
  onClick, 
  onDelete, 
  onEdit, 
  onShare, 
  onSetDefault, 
  onUnfollow,
  onTransferOwnership,
  isDefault = false, 
  isOwner = true,
  statsRefreshTrigger = 0
}) => {
  const [stats, setStats] = useState({
    totalItems: 0,
    purchasedItems: 0,
    pendingItems: 0,
    progress: 0
  });
  const [showMenu, setShowMenu] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showUnfollowConfirm, setShowUnfollowConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { t } = useLanguage();
  const menuRef = useRef(null);

  useEffect(() => {
    // Add a small delay to prevent rapid consecutive updates from causing flickering
    const timer = setTimeout(() => {
      loadStats();
    }, 100);
    
    return () => clearTimeout(timer);
  }, [list.id, statsRefreshTrigger]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  const loadStats = async () => {
    try {
      setLoading(true);
      const statsData = await apiService.getListStats(list.id);
      setStats(statsData);
    } catch (err) {
      console.error('Error loading stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    setShowDeleteConfirm(true);
    setShowMenu(false);
  };

  const handleShare = (e) => {
    e.stopPropagation();
    onShare();
    setShowMenu(false);
  };

  const handleSetDefault = (e) => {
    e.stopPropagation();
    onSetDefault();
    setShowMenu(false);
  };

  const handleUnfollow = (e) => {
    e.stopPropagation();
    setShowUnfollowConfirm(true);
    setShowMenu(false);
  };

  const handleTransferOwnership = (e) => {
    e.stopPropagation();
    onTransferOwnership();
    setShowMenu(false);
  };

  const confirmDelete = () => {
    onDelete();
  };

  const confirmUnfollow = () => {
    onUnfollow();
  };

  const handleMenuClick = (e) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}.${month} ${hours}:${minutes}`;
  };

  return (
    <div className="shopping-list-card" onClick={onClick}>
      {/* Header with title, description, and menu */}
      <div className="card-header">
        <div className="card-title-section">
          <div className="title-with-badges">
            <h3 className="card-title">{list.name}</h3>
            {list.isShared && (
              <span className="shared-badge" title={t('sharedList', 'Shared list')}>
                <Users size={12} />
                {t('shared', 'Shared')}
              </span>
            )}
          </div>
          {list.description && (
            <p className="card-description">{list.description}</p>
          )}
        </div>
        
        <button 
          className="card-menu-btn"
          onClick={handleMenuClick}
        >
          <MoreVertical size={16} />
        </button>
        
        {showMenu && (
          <div className="card-menu" ref={menuRef}>
            {isOwner && (
              <>
                <button onClick={handleSetDefault} className="menu-item">
                  {isDefault ? <StarOff size={16} /> : <Star size={16} />}
                  {isDefault ? t('removeDefault', 'Remove Default') : t('setAsDefault', 'Set as Default')}
                </button>
                <button onClick={handleShare} className="menu-item">
                  <Share2 size={16} />
                  {t('shareList', 'Share List')}
                </button>
                <button onClick={handleTransferOwnership} className="menu-item">
                  <UserCheck size={16} />
                  {t('transferOwnership', 'Transfer Ownership')}
                </button>
                <button onClick={handleDelete} className="menu-item delete">
                  <Trash2 size={16} />
                  {t('delete', 'Delete')}
                </button>
              </>
            )}
            {!isOwner && (
              <>
                <div className="menu-item disabled">
                  <Users size={16} />
                  {t('sharedBy', 'Shared by')} {list.owner}
                </div>
                <button onClick={handleUnfollow} className="menu-item delete">
                  <UserMinus size={16} />
                  {t('unfollow', 'Unfollow')}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Progress Section */}
      <div className="card-progress-section">
        {loading ? (
          <div className="loading-shimmer progress-shimmer"></div>
        ) : (
          <>
            <div className="progress-bar-container">
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ width: `${stats.progress}%` }}
                ></div>
              </div>
              <span className="progress-percentage">{stats.progress}%</span>
            </div>
          </>
        )}
      </div>

      {/* Stats Section */}
      <div className="card-stats-section">
        {loading ? (
          <div className="loading-shimmer stats-shimmer"></div>
        ) : (
          <div className="stats-row">
            <div className="stats-left">
              <span className="total-items">
                <Circle size={14} className="unchecked-icon" />
                {stats.totalItems} {t('items', 'items')} 
                <CheckCircle size={14} className="checked-icon" />
                {stats.purchasedItems || 0} {t('checked', 'checked')}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Footer Section */}
      <div className="card-footer-section">
        <div className="footer-content">
          <div className="footer-left">
            <span className="last-updated-time">
              {formatDate(list.updatedAt)}
            </span>
            
            {(list.isShared || list.hasShares) && (
              <span className="collaborative-text" title={t('collaborativeList', 'Collaborative list')}>
                <Users size={12} />
                {list.isShared && list.canWrite === false 
                  ? t('viewOnly', 'View Only') 
                  : t('collaborative', 'Collaborative')}
              </span>
            )}
          </div>
          
          <div className="footer-badges">
            {isDefault && (
              <span className="footer-badge default-badge" title={t('defaultList', 'Default list')}>
                <Star size={12} />
                {t('default', 'Default')}
              </span>
            )}
            
            {!loading && (
              <span className={`footer-badge status-badge ${
                stats.progress === 100 ? 'complete' : 
                stats.totalItems === 0 ? 'empty' : 'active'
              }`}>
                {stats.progress === 100 ? t('complete', 'Complete') : 
                 stats.totalItems === 0 ? t('empty', 'Empty') : 
                 t('active', 'Active')}
              </span>
            )}
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDelete}
        title={t('deleteList', 'Delete List')}
        message={t('confirmDeleteList', 'Are you sure you want to delete this list?')}
        confirmText={t('delete', 'Delete')}
        danger={true}
      />

      <ConfirmModal
        isOpen={showUnfollowConfirm}
        onClose={() => setShowUnfollowConfirm(false)}
        onConfirm={confirmUnfollow}
        title={t('unfollow', 'Unfollow')}
        message={t('confirmUnfollow', 'Are you sure you want to stop following this list?')}
        confirmText={t('unfollow', 'Unfollow')}
        danger={false}
      />
    </div>
  );
};

export default React.memo(ShoppingListCard, (prevProps, nextProps) => {
  // Only re-render if essential props have changed
  return (
    prevProps.list.id === nextProps.list.id &&
    prevProps.list.name === nextProps.list.name &&
    prevProps.list.updatedAt === nextProps.list.updatedAt &&
    prevProps.statsRefreshTrigger === nextProps.statsRefreshTrigger &&
    prevProps.isOwner === nextProps.isOwner &&
    prevProps.isDefault === nextProps.isDefault
  );
});
